import { PROJECT_STAGES, STATE_FUNNEL, type ProjectStage } from '@stages/stages';

/**
 * Mapping event_type -> root status label used in DateLogCell.
 * Only root events are included; substatus/detail events are intentionally excluded.
 */
export const EVENT_TO_BASE_STATE: Record<string, ProjectStage> = {
  added: 'Добавлен',
  recording_started: 'Запись',
  recording_completed: 'Монтаж',
  review_sent: 'Проверка',
  revisions_opened: 'Правки',
  corrections_sent_to_review: 'Проверка правок',
  project_completed: 'Готов',
};

/** Display order in the dates cell (top to bottom): последний этап цепочки сверху. */
export const STATE_DISPLAY_ORDER: readonly ProjectStage[] = [...PROJECT_STAGES].reverse();

export type StatusLogStateLabel = ProjectStage;

/**
 * Event used to create a date row when an event is missing.
 * Must map back to the same row via EVENT_TO_BASE_STATE.
 */
export const BASE_STATE_PRIMARY_EVENT: Record<StatusLogStateLabel, string> = {
  Готов: 'project_completed',
  'Проверка правок': 'corrections_sent_to_review',
  Правки: 'revisions_opened',
  Проверка: 'review_sent',
  Монтаж: 'recording_completed',
  Запись: 'recording_started',
  Добавлен: 'added',
};

/**
 * All project_status_events event types for the date-log editor (latest stages first).
 * `corrections_pre_archive` — устаревший тип: старые строки остаются в базе, но новых нет и править их нельзя.
 */
export const STATUS_LOG_EDIT_EVENT_ORDER = [
  'project_unarchived',
  'project_archived',
  'project_fully_paid',
  'project_partially_paid',
  'project_completed',
  'corrections_review_finished',
  'corrections_sent_to_review',
  'corrections_applied',
  'corrections_recorded',
  'review_completed',
  'revisions_opened',
  'review_finished',
  'review_started',
  'review_sent',
  'editing_completed',
  'editing_started',
  'recording_completed',
  'recording_finished',
  'recording_started',
  'added',
] as const;

export type StatusLogEditEventType = (typeof STATUS_LOG_EDIT_EVENT_ORDER)[number];

/** События архива: вне воронки state, ставятся через is_archived (сервер syncProjectArchiveStatusLog) */
export type StatusArchiveEventType = 'project_archived' | 'project_unarchived';

const ARCHIVE_EVENT_TYPES: ReadonlySet<string> = new Set<StatusArchiveEventType>(['project_archived', 'project_unarchived']);

/** Воронка событий производства по порядку (как на сервере): от `added` до `project_fully_paid`; события архива — вне воронки. */
export type StatusEventFunnelType = Exclude<StatusLogEditEventType, StatusArchiveEventType>;

export const STATUS_EVENT_FUNNEL_ORDER: readonly StatusEventFunnelType[] = STATUS_LOG_EDIT_EVENT_ORDER.filter(
  (type): type is StatusEventFunnelType => !ARCHIVE_EVENT_TYPES.has(type)
).reverse();

/** Подписи событий в окне дат, ячейке дат и истории проекта (by event_type) — единственный источник. */
export const STATUS_EVENT_TYPE_LABELS: Record<StatusLogEditEventType, string> = {
  added: 'Дата добавления',
  recording_started: 'Дата начала записи',
  recording_finished: 'Дата окончания записи',
  recording_completed: 'Дата передачи на монтаж',
  editing_started: 'Дата начала монтажа',
  editing_completed: 'Дата завершения монтажа',
  review_sent: 'Дата отправки на проверку',
  review_started: 'Дата начала проверки',
  review_finished: 'Дата завершения проверки',
  revisions_opened: 'Дата передачи на правки',
  review_completed: 'Дата подготовки правок',
  corrections_recorded: 'Дата записи правок',
  corrections_applied: 'Дата внесения правок',
  corrections_sent_to_review: 'Дата отправки правок на проверку',
  corrections_review_finished: 'Дата окончания проверки правок',
  project_completed: 'Дата сдачи проекта',
  project_partially_paid: 'Дата частичной оплаты',
  project_fully_paid: 'Дата полной оплаты',
  project_archived: 'Дата архивации',
  project_unarchived: 'Дата деархивации',
};

/** Событие воронки по состоянию: n-е состояние STATE_FUNNEL пишет n-е событие воронки (соответствие закреплено тестом сервера) */
const STATE_FUNNEL_EVENT: ReadonlyMap<string, StatusEventFunnelType> = new Map(
  STATE_FUNNEL.map((state, i) => [state, STATUS_EVENT_FUNNEL_ORDER[i]!])
);

/** Событие, которое записал переход проекта на это состояние; null — состояние вне воронки или не задано */
export function getStateFunnelEvent(state: string | null | undefined): StatusEventFunnelType | null {
  return (state && STATE_FUNNEL_EVENT.get(state)) || null;
}
